<template>
  <div>
    <Modal
      v-if="open || autoOpen"
      :title="modalTitle"
      @primary-action="save"
      @hide-modal="closeModal"
    >
      <template #header>
        <div v-if="editingSeries">
          <Alert type="warning" class="mt-3">
            <span>
              You are editing the event series and not the individual event.
              Editing or canceling this event will impact all events in this
              series.
            </span>
          </Alert>
        </div>
      </template>
      <div class="flex flex-col gap-4">
        <TextField
          v-model="event.name"
          label="Name"
          class="w-full"
          name="Name"
          placeholder="Event Name"
          :error-message="firstErrorMessage(v$.name)"
        ></TextField>
        <Select
          v-model="technicianIds"
          label="Technician(s)"
          multiple
          placeholder="All Techs"
          data-cy="technicians"
          :options="technicianOptions"
        ></Select>
        <Checkbox
          v-model="available"
          class="mb-0"
          label="Available for customer appointments"
        ></Checkbox>
        <divider class="my-0"></divider>
        <Checkbox
          v-model="event.all_day"
          class="mb-0"
          label="All Day"
        ></Checkbox>
        <div class="date_times flex flex-col gap-3">
          <div class="flex flex-col md:flex-row gap-3">
            <div class="flex items-start gap-3">
              <Datepicker
                v-model="startDate"
                class="mb-0"
                :label="startLabel"
                data-cy="startDate"
                :field-error-message="firstErrorMessage(v$.startDate)"
              />
              <TimePicker
                v-if="!event.all_day"
                v-model="startTime"
                class="time-picker mb-0"
                :format="TIME_FORMAT"
                :input-format="TIME_FORMAT"
                placeholder="--"
                label=" "
                data-cy="startTime"
                :field-error="firstErrorMessage(v$.startTime)"
              />
            </div>
            <div class="flex items-start gap-3">
              <Datepicker
                v-model="endDate"
                :label="endLabel"
                class="mb-0"
                data-cy="endDate"
                :field-error-message="firstErrorMessage(v$.endDate)"
              />
              <TimePicker
                v-if="!event.all_day"
                v-model="endTime"
                class="time-picker mb-0"
                :format="TIME_FORMAT"
                :input-format="TIME_FORMAT"
                placeholder="--"
                label=" "
                data-cy="endTime"
                :field-error="firstErrorMessage(v$.endTime)"
              />
            </div>
          </div>
          <div class="flex flex-col gap-3 w-full">
            <Checkbox
              v-model="recurring"
              class="flex-inline mb-0"
              label="Recurring"
              :disabled="!startDate"
            ></Checkbox>
            <RecurrenceForm
              v-if="recurring"
              v-model="recurrence"
              :preset="event.recurrence_preset"
              :start-date="momentStartDate"
              :v$="v$"
              @preset-changed="handlePresetChanged"
            />
          </div>
        </div>
        <pre v-if="DEBUG">{{ JSON.stringify(payloadRef, null, 2) }}</pre>
      </div>
      <template #footer>
        <div
          v-if="isEdit"
          class="flex flex-row w-full justify-content-end mb-4 gap-3 button-container pt-4 border-t md:border-t-0"
        >
          <Button type="danger" :disabled="saving" @click="handleDelete">{{
            deleteLabel
          }}</Button>
          <Button :disabled="saving" :saving="saving" @click="handleSubmit">{{
            saveLabel
          }}</Button>
        </div>
        <div
          v-else
          class="flex flex-row w-full justify-content-end mb-4 button-container pt-4 border-t md:border-t-0"
        >
          <Button :disabled="saving" :saving="saving" @click="handleSubmit"
            >Add Event</Button
          >
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import moment from "moment";
import Select from "serviceshift-ui/components/Inputs/Select.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Button from "@/components/Button.vue";
import Checkbox from "@/components/Checkbox.vue";
import Datepicker from "@/components/Datepicker.vue";
import Divider from "@/components/Divider.vue";
import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import TextField from "@/components/TextField.vue";
import TimePicker from "@/components/TimePicker.vue";
import { getCombinedDateTime } from "@/mixins";
import { useCalendarEvents } from "@/Scheduler/CalendarEvents/useCalendarEvents";
import {
  CalendarEvent,
  RecurrencePresetName,
  RecurrenceRule,
  TIME_FORMAT
} from "@/typings/calendarEvent";
import { getRecurrencePreset } from "@/typings/calendarEventForm";
import useVuelidate from "@vuelidate/core";
import {
  helpers,
  maxLength,
  required,
  requiredIf
} from "@vuelidate/validators";
import RecurrenceForm from "./CalendarEventModal/forms/RecurrenceForm.vue";

const {
  fetchTechnicians,
  technicianOptions,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent
} = useCalendarEvents(true);

const DEBUG = false;

const store = useStore();
const saving = ref(false);

const DEFAULT_PRESET = RecurrencePresetName.weekly;

const emit = defineEmits(["new-event", "close", "hide-modal", "created"]);

const props = defineProps({
  calendarEvent: {
    type: Object as () => CalendarEvent,
    required: false,
    default: () => {
      return new CalendarEvent();
    }
  },
  open: {
    type: Boolean,
    required: false,
    default: false
  },
  autoOpen: {
    type: Boolean,
    default: true
  },
  calendarEventId: {
    type: [Number, String],
    default: null
  }
});

const event = ref(props.calendarEvent);

function getRecurrence(): RecurrenceRule {
  // load the defaults from the preset
  let recurrence_rule = getRecurrencePreset(
    event.value.recurrence_preset || DEFAULT_PRESET
  )!.toRecurrenceRule();
  // overlay stored properties
  if (event.value.recurrence_rules && event.value?.recurrence_rules[0]) {
    recurrence_rule = {
      ...recurrence_rule,
      ...event.value.recurrence_rules[0]
    };
  }

  return recurrence_rule;
}

function initializeForm() {
  event.value = { ...props.calendarEvent, ...event.value };
  const rawIds =
    event.value.technicianIds ||
    event.value.participants?.map((p) => p.user_id) ||
    event.value.participants_attributes?.map((p) => p.participant_id);
  technicianIds.value = rawIds ? [...new Set(rawIds)] : rawIds;
  available.value = !event.value.exclusive;
  startDate.value = event.value.starts_at;
  startTime.value = moment(event.value.starts_at).format("h:mm a");
  endDate.value = event.value.ends_at;
  endTime.value = moment(event.value.ends_at).format("h:mm a");
  recurring.value = event.value.recurring;
  savedRecurring.value = event.value.recurring;
  recurrence.value = getRecurrence();
  v$.value.$reset();
}

watch(
  () => props.calendarEvent,
  () => initializeForm()
);

watch(event, (newVal, oldVal) => {
  if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
    initializeForm();
  }
});

const available = ref<boolean>(!event.value.exclusive);
watch(available, (newValue) => {
  event.value.exclusive = !newValue;
});

const recurring = ref<boolean>(event.value.recurring);
const savedRecurring = ref<boolean>(event.value.recurring);
watch(recurring, (newValue) => {
  event.value.recurring = newValue;

  if (newValue) {
    event.value.recurrence_preset ??= DEFAULT_PRESET;
  }

  if (!newValue) {
    recurrence.value = getRecurrence();
    event.value.recurrence_preset = DEFAULT_PRESET;
    event.value.recurrence_rules = [];
  }
});

const recurrence = ref<RecurrenceRule>(getRecurrence());

const technicianIds = ref<number[] | null>(event.value.technicianIds);
watch(technicianIds, (newValue) => {
  if (newValue == null) {
    event.value.participants_attributes = [];
    return;
  }
  if (!newValue) return;
  event.value.participants_attributes = newValue.map(
    (participant_id: number) => {
      return { participant_type: "User", participant_id };
    }
  );
});

const startDate = ref<Date>(event.value.starts_at);
watch(startDate, (newValue) => {
  event.value.starts_at = getCombinedDateTime(
    newValue,
    startTime.value ?? "00:00 a"
  ).toDate();
});

const startTime = ref<string | null>(event.value.startTime);
watch(startTime, (newValue) => {
  event.value.starts_at = getCombinedDateTime(
    startDate.value,
    newValue ?? "00:00 a"
  ).toDate();
});

const endDate = ref<Date>(event.value.ends_at);
watch(endDate, (newValue) => {
  event.value.ends_at = getCombinedDateTime(
    newValue,
    endTime.value ?? "23:59 p"
  ).toDate();
});

const endTime = ref<string | null>(event.value.endTime);
watch(endTime, (newValue) => {
  event.value.ends_at = getCombinedDateTime(
    endDate.value,
    newValue ?? "23:59 p"
  ).toDate();
});

const open = ref(props.open);
watch(
  () => props.open,
  (newValue) => {
    open.value = newValue;
    if (newValue) {
      saving.value = false;
    }
  }
);

const startLabel = computed(() => {
  return event.value.all_day ? "Start Date" : "Start Time";
});

const endLabel = computed(() => {
  return event.value.all_day ? "End Date" : "End Time";
});

const modalTitle = computed(() => {
  if (!isEdit.value) {
    return "Create New Event";
  }

  return savedRecurring.value ? "Edit Event Series" : "Edit Event";
});

const editingSeries = computed(() => {
  return isEdit.value && savedRecurring.value;
});

const saveLabel = computed(() => {
  return editingSeries.value ? "Save Event Series" : "Save Event";
});

const deleteLabel = computed(() => {
  return editingSeries.value ? "Delete Event Series" : "Delete Event";
});

const momentStartDate = computed(() => {
  if (!startDate.value) return null;
  return moment(startDate.value);
});

const isEdit = computed(() => {
  return !!event.value.id;
});

const payloadRef = ref({});

// reset the form when the modal closes
watch(open, (newValue) => {
  if (newValue) {
    initializeForm();
  }
});

// Toggle recurring options when start date is not present
watch(startDate, (newValue) => {
  if (!newValue) {
    recurring.value = false;
  }
});

watch(recurrence, (newValue) => {
  if (newValue) {
    event.value.recurrence_rules = [newValue];
  } else {
    event.value.recurrence_rules = [];
  }
});

function handlePresetChanged(newValue: RecurrencePresetName) {
  event.value.recurrence_preset = newValue;
}

onMounted(async () => {
  fetchTechnicians();
  if (props.calendarEventId) {
    await fetchCalendarEvent();
  }
  if (props.calendarEvent) {
    initializeForm();
  }
});

const fetchCalendarEvent = async () => {
  event.value = await store.dispatch(
    "fetchCalendarEvent",
    props.calendarEventId
  );
};

// begin validation
const { withMessage } = helpers;

const validationState = {
  name: computed(() => {
    return event.value.name;
  }),
  startDate,
  startTime,
  endDate,
  endTime,
  endsOn: computed(() => {
    return event.value.recurrence_rules.at(0)?.until;
  })
};

const endsOnIsAfterStart = () => {
  const rrule = event.value.recurrence_rules.at(0);

  if (!rrule || rrule?.ends != "on") {
    // ends "on" is not selected and does not need to be validated
    return true;
  }

  const endsOn = rrule?.until;

  if (event.value.all_day) {
    if (!startDate.value || !endsOn) {
      return true;
    }
    return moment(startDate.value).diff(endsOn, "days") <= 0;
  } else {
    if (!startDate.value || !startTime.value || !endsOn) {
      return true;
    }
    const start = getCombinedDateTime(startDate.value, startTime.value);
    const end = moment(endsOn);
    return start.diff(end, "minutes") < 0;
  }
};

const startBeforeEnd = () => {
  if (event.value.all_day) {
    if (!startDate.value || !endDate.value) {
      return true;
    }
    return moment(startDate.value).diff(endDate.value, "days") <= 0;
  } else {
    if (
      !startDate.value ||
      !startTime.value ||
      !endDate.value ||
      !endTime.value
    ) {
      return true;
    }
    const start = getCombinedDateTime(startDate.value, startTime.value);
    const end = getCombinedDateTime(endDate.value, endTime.value);
    return start.diff(end, "minutes") < 0;
  }
};

const v$ = useVuelidate(
  {
    name: {
      required: withMessage("Name is required", required),
      length: withMessage("Maximum 100 characters allowed", maxLength(100))
    },
    startDate: {
      required: withMessage("Date is required", required)
    },
    startTime: {
      requiredIf: withMessage(
        "Start Time is required",
        requiredIf(() => !event.value.all_day)
      )
    },
    endDate: {
      requiredIf: withMessage("End Date is required", required),
      beforeEnd: withMessage("Event end must be after start", startBeforeEnd)
    },
    endTime: {
      requiredIf: withMessage(
        "End Time is required",
        requiredIf(() => !event.value.all_day)
      ),
      beforeEnd: withMessage(" ", startBeforeEnd)
    },
    endsOn: {
      requiredIf: withMessage(
        "Required",
        requiredIf(() => {
          return event.value.recurrence_rules.at(0)?.ends == "on";
        })
      ),
      afterStart: withMessage("Must be after event start", endsOnIsAfterStart)
    }
  },
  validationState
);
// end validation

function closeModal() {
  saving.value = false;
  emit("close");
  emit("hide-modal");
}

async function validate() {
  return v$.value.$validate();
}

const handleDelete = debounce(
  async () => {
    await deleteEvent();
  },
  250,
  { immediate: true }
);

const handleSubmit = debounce(
  async () => {
    await save();
  },
  250,
  { immediate: true }
);

async function deleteEvent() {
  await deleteCalendarEvent({
    ...event.value,
    recurring: savedRecurring.value
  });

  closeModal();
}

async function save() {
  if (saving.value) return;

  const valid = await validate();

  if (!valid) {
    return;
  }

  const payload: CalendarEvent = cloneDeep(event.value);

  if (!payload.recurring) {
    payload.recurrence_preset = undefined;
    payload.recurrence_rules = [];
  }

  if (payload.all_day) {
    const start_moment = moment(payload.starts_at);
    start_moment.set({ hour: 0, minute: 0, second: 0 });
    payload.starts_at = start_moment.toDate();

    const end_moment = moment(payload.ends_at);
    end_moment.set({ hour: 23, minute: 59, second: 59 });
    payload.ends_at = end_moment.toDate();
  }

  if (payload.recurring) {
    const rrule = payload.recurrence_rules[0];

    // we carry both "count" and "until" through the UI so that the controls stay populated,
    // but we only want to send the selected one to the server, since it supports setting both at once
    switch (rrule.ends) {
      case "never":
        rrule.count = undefined;
        rrule.until = undefined;
        break;
      case "after":
        rrule.until = undefined;
        break;
      case "on":
        rrule.count = undefined;
        break;
    }
  }

  payloadRef.value = payload;

  saving.value = true;
  try {
    if (DEBUG) {
      window.notify.success("[[DEBUG MODE]] Validated OK");
      return;
    }

    if (isEdit.value) {
      await updateCalendarEvent(payload as CalendarEvent);
    } else {
      await createCalendarEvent(payload as CalendarEvent);
    }
    emit("created");
    closeModal();
  } catch (ex: any) {
    const error = ex as Error;
    console.log("An error occurred: " + error.message);
  } finally {
    saving.value = false;
  }
}
</script>

<style lang="scss" scoped>
:deep(.label) {
  font-weight: bold;
}
:deep(.textfield-label) {
  margin-bottom: 0;
}
.flex-inline {
  display: inline-flex !important;
}
.button-container {
  padding-right: 30px;
}
label {
  width: fit-content;
}
</style>
